import { FC, useRef } from 'react'
import { useRequest } from 'ahooks'
import { jwtDecode } from 'jwt-decode'

import { toast } from '$app/utilities'

import useRouter from '$actions/useRouter'
import use2FAHelper from '$actions/use2FAHelper'
import useMFABlock from '$actions/useMFABlock'
import useCreateAuthRTC from '$actions/useCreateAuthRTC'

import Link from '$elements/Link'
import SeparatorWithLabel from '$elements/SeparatorWithLabel'

import { suggestedLink, suggestedLinkContainer } from '$blocks/common/auth.css'
import MFABlock from '$blocks/MFABlock'

import ForgotPasswordForm, {
  ForgotPasswordFormProps
} from '$hook-forms/ForgotPasswordForm'

import { ToastMessage } from '$app/types'
import { asyncSuccessResolver } from '$app/utilities'

import { DecodedToken } from '$store/session/tokens'

import { AnonymousAuthAPI } from '$services/api/anonymous-auth'
import { MfaAuthAPI } from '$services/api/mfa-auth'
import { AuthAPI } from '$services/api/auth'
import { parse2faGuardStagedContentResolver } from '$services/api/resolvers'
import { successResolver, errorMessageResolver } from '$services/api/common'

import {
  container,
  description,
  header,
  loginLinkLabel,
  title
} from './styles.css'

const toastMsg = {
  loading: 'Requesting..',
  error: errorMessageResolver,
  success: 'Reset link sent! Check your email to continue.'
} satisfies ToastMessage

export const ForgotPassword: FC = () => {
  const { redirectWithQueryParams } = useRouter()

  const { current: anonymousApi } = useRef(new AnonymousAuthAPI(null))
  const { current: authAPI } = useRef(new AuthAPI(null))
  const { current: mfaApi } = useRef(new MfaAuthAPI(null))
  const [authRTC, createAuthRTC] = useCreateAuthRTC()

  const setAuthToken: typeof anonymousApi.updateToken = token => {
    anonymousApi.updateToken(token)
    authAPI.updateToken(token)
    mfaApi.updateToken(token)

    if (!token) return

    const { jti: token_id, sub: user_id } = jwtDecode<DecodedToken>(token)

    createAuthRTC({ authAPI, token_id, user_id })
  }

  const { forgotPassword, forgotPassword2FA } = anonymousApi

  const forgotPasswordRequest = useRequest(
    asyncSuccessResolver(forgotPassword, successResolver),
    { manual: true }
  )
  const forgotPassword2FARequest = useRequest(
    asyncSuccessResolver(forgotPassword2FA, successResolver),
    {
      manual: true,
      onSuccess: () => {
        onRequested()
      }
    }
  )

  const loading =
    forgotPasswordRequest.loading || forgotPassword2FARequest.loading

  const twoFAHelper = use2FAHelper({ getMfaBlock: () => mfaBlock })
  const preferred_factor = twoFAHelper.getPreferredFactorByContent(
    forgotPasswordRequest.data?.data
  )
  const available_factors = twoFAHelper.getFactorsByContent(
    forgotPasswordRequest.data?.data
  )

  const mfaBlock = useMFABlock({
    apiInstance: mfaApi,
    authRTC,
    data: { available_factors, preferred_factor },
    onVerified: mfa_token => {
      const { runAsync } = forgotPassword2FARequest

      toast
        .promise(runAsync({ mfa_token }), toastMsg)
        .unwrap()
        .then(onRequested)
        .catch(twoFAHelper.reChallengeOrExitOnError)
    }
  })

  const onRequested = () => {
    // Retain ?return_to query
    redirectWithQueryParams('authLogin')
  }

  const onSuccess: ForgotPasswordFormProps['onSuccess'] = async (
    reset,
    data
  ) => {
    const { runAsync } = forgotPasswordRequest

    const result = await toast
      .promise(runAsync({ ...data, request_origin: self.origin }), {
        ...toastMsg,
        success: ({ data, message }) => {
          const { auth_token } = {
            ...parse2faGuardStagedContentResolver(data)
          }

          return auth_token ? message : toastMsg.success
        }
      })
      .unwrap()

    reset()

    if (!result) {
      onRequested()

      return
    }

    const { auth_token, status } = {
      ...parse2faGuardStagedContentResolver(result.data)
    }

    if (auth_token && status == '2fa_required') {
      setAuthToken(auth_token)
      mfaBlock.modalControls.open()
    }
  }

  return (
    <section className={container}>
      <header className={header}>
        <h2 className={title}>Get password reset link</h2>

        <p className={description}>
          We will send the reset password link to your email inbox.
        </p>
      </header>

      <ForgotPasswordForm onSuccess={onSuccess} disabled={loading} />
      <MFABlock {...mfaBlock} />

      <div className={suggestedLinkContainer}>
        <SeparatorWithLabel label="OR" />

        <p>
          <span className={loginLinkLabel}>Remember your password?</span>{' '}
          <Link
            to="authLogin"
            // Retain ?return_to query
            options={({ queryParams }) => ({ queryParams })}
            className={suggestedLink}
          >
            Click here to log in.
          </Link>
        </p>
      </div>
    </section>
  )
}

export default ForgotPassword
